<template>
  <div class="availability-form-unavailability">
    <div class="space-y-4">
      <div class="flex items-center justify-between gap-x-2">
        <span class="text-sm font-semibold select-none">Afwezigheid</span>
        <div class="flex items-center gap-x-2">
          <div v-if="pastItemsCount > 0" class="flex items-center gap-x-2">
            <GeneralButton
              :color="showPastItems ? 'primary' : 'primary-lighten-1'"
              :icon="['fas', 'clock-rotate-left']"
              size="small"
              :title="
                showPastItems
                  ? 'Afwezigheid in het verleden verbergen'
                  : 'Afwezigheid in het verleden tonen'
              "
              @click="togglePastItems"
            >
              <span>In het verleden ({{ pastItemsCount }})</span>
            </GeneralButton>
          </div>
          <GeneralButton
            @click="addNewAvailabilityBlock"
            :icon="['fas', 'plus']"
            title="Afwezigheid toevoegen"
            size="small"
            :disabled="disabled"
          >
            <span>Afwezigheid toevoegen</span>
          </GeneralButton>
        </div>
      </div>

      <TransitionGroup name="unavailability-list" tag="div" class="relative space-y-2">
        <template v-for="(entry, i) in sortedAvailabilityBlocks" :key="entry.item.id ?? `new-${i}`">
          <div
            v-if="showPastItems && i === firstPastIndex && firstPastIndex > 0"
            ref="pastDividerEl"
            :key="'divider'"
            class="flex items-center gap-x-2 py-1"
          >
            <!-- Divider for past items -->
            <div class="h-px flex-1 bg-gray-300" />
            <span class="text-xs text-gray-400 select-none">Afwezigheid in het verleden</span>
            <div class="h-px flex-1 bg-gray-300" />
          </div>

          <div v-if="isVisible(entry.item)">
            <AvailabilityFormUnavailabilityItem
              v-model="availabilityBlocks[entry.originalIndex]"
              :availability-reasons="availabilityReasons"
              :disabled="disabled"
              @has-error="onHasError"
              @save="updateSortedBlocks"
            />
          </div>
        </template>
      </TransitionGroup>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import moment from "moment"
import { AvailabilityBlock } from "@/models/availability-block.model.ts"
import AvailabilityFormUnavailabilityItem from "@/components/availability/form/AvailabilityFormUnavailabilityItem.vue"
import type { AvailabilityBucket } from "@/models/availability-bucket.model.ts"
import type { IAvailabilityBlock } from "@/interfaces/api/availability-block.interface.ts"
import { AvailabilityTypes } from "@/enums/models/availability-types.ts"
import { useAvailabilityReasonStore } from "@/store/availability-reasons.store.ts"

const props = withDefaults(
  defineProps<{
    availabilityBucket: AvailabilityBucket
    entityTable: string
    disabled?: boolean
  }>(),
  {
    disabled: false
  }
)

const availabilityBlocks = defineModel<AvailabilityBlock[]>({ required: true })

const availabilityReasons = useAvailabilityReasonStore().getAvailabilityReasonsForEntityTable(
  props.entityTable
)

function addNewAvailabilityBlock() {
  availabilityBlocks.value.push(
    new AvailabilityBlock({
      availabilityBucket: props.availabilityBucket.id!,
      availabilityType: AvailabilityTypes.UNAVAILABLE
    } as IAvailabilityBlock)
  )
}
const showPastItems = ref(false)
const pastDividerEl = ref<HTMLElement[]>([])

async function togglePastItems() {
  showPastItems.value = !showPastItems.value

  if (!showPastItems.value) {
    return
  }

  await nextTick()

  pastDividerEl.value[0]?.scrollIntoView({ behavior: "smooth", block: "start" })
}
function isPastItem(item: AvailabilityBlock) {
  const today = moment().startOf("day")

  // Primary: if timeEnd is set, use it to determine if the item is in the past.
  if (item.timeEnd) {
    return item.timeEnd.isBefore(today)
  }

  // Else, the event has not ended yet.
  return false
}

const errorItemIds = ref(new Set<string>())

function onHasError(id: string, value: boolean) {
  if (value) {
    errorItemIds.value.add(id)
  } else {
    errorItemIds.value.delete(id)
  }
}

function isVisible(item: AvailabilityBlock) {
  // Hide items with available status (currently reserved for top of PersonModelEditFormAvailability).
  if (item.availabilityType === AvailabilityTypes.AVAILABLE) {
    return false
  }

  // Show all items when past items are shown.
  if (showPastItems.value) {
    return true
  }

  // Show new or error items even if they are in the past.
  if (!item.id || errorItemIds.value.has(item.id)) {
    return true
  }

  // Hide past items.
  return !isPastItem(item)
}

type SortedAvailabilityWrapper = { item: AvailabilityBlock; originalIndex: number }
const sortedAvailabilityBlocks = ref<SortedAvailabilityWrapper[]>([])
const unavailabilityBlocks = computed(() =>
  availabilityBlocks.value
    .map((item, originalIndex) => ({ item, originalIndex }))
    .filter(({ item }) => item.availabilityType === AvailabilityTypes.UNAVAILABLE)
)

function sortAvailabilityBlocks(items: AvailabilityBlock[]): SortedAvailabilityWrapper[] {
  // Sorting logic:
  // - New items (no timeStart or timeEnd) come first
  // - Items with only timeStart come before items with timeEnd
  // - Items with timeEnd come before past items
  // - Past items are sorted by timeEnd (most recent first)
  // TODO 2755: determine best sorting logic (currently kind of opinionated)
  const indexed = items.map((item, originalIndex) => ({ item, originalIndex }))

  indexed.sort((a, b) => {
    const aPast = isPastItem(a.item)
    const bPast = isPastItem(b.item)

    // Current/future items come before past items
    if (aPast !== bPast) {
      return aPast ? 1 : -1
    }

    if (!aPast) {
      // --- Sorting for current/future items ---
      const aBothNull = !a.item.timeStart && !a.item.timeEnd
      const bBothNull = !b.item.timeStart && !b.item.timeEnd

      // 1. Both dates null (newly created) on top
      if (aBothNull !== bBothNull) {
        return aBothNull ? -1 : 1
      }
      if (aBothNull && bBothNull) {
        return 0
      }

      const aHasEnd = !!a.item.timeEnd
      const bHasEnd = !!b.item.timeEnd

      // 2. Items with only timeStart (no timeEnd) come before items with timeEnd
      if (aHasEnd !== bHasEnd) {
        return aHasEnd ? 1 : -1
      }

      if (!aHasEnd && !bHasEnd) {
        // Both have only timeStart: closest to today on top
        return a.item.timeStart!.valueOf() - b.item.timeStart!.valueOf()
      }

      // 3. Both have timeEnd: closest timeEnd on top
      return a.item.timeEnd!.valueOf() - b.item.timeEnd!.valueOf()
    } else {
      // --- Sorting for past items: most recent first ---
      const aDate = a.item.timeEnd ?? a.item.timeStart
      const bDate = b.item.timeEnd ?? b.item.timeStart

      if (!aDate && !bDate) {
        return 0
      }
      if (!aDate) {
        return 1
      }
      if (!bDate) {
        return -1
      }

      return bDate.valueOf() - aDate.valueOf()
    }
  })

  return indexed
}

function updateSortedBlocks() {
  sortedAvailabilityBlocks.value = sortAvailabilityBlocks(
    unavailabilityBlocks.value.map((entry) => entry.item)
  ).map((entry) => ({
    item: entry.item,
    originalIndex: unavailabilityBlocks.value[entry.originalIndex]!.originalIndex
  }))
}

// Re-sort when items are added or removed (e.g. new item, delete)
watch(() => availabilityBlocks.value.length, updateSortedBlocks, { immediate: true })

const firstPastIndex = computed(() =>
  sortedAvailabilityBlocks.value.findIndex((entry) => isPastItem(entry.item))
)

const pastItemsCount = computed(
  () => unavailabilityBlocks.value.filter(({ item }) => isPastItem(item)).length
)
</script>

<style scoped>
.unavailability-list-move,
.unavailability-list-enter-active,
.unavailability-list-leave-active {
  transition: all 0.35s ease;
}

.unavailability-list-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.unavailability-list-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.unavailability-list-leave-active {
  position: absolute;
  width: 100%;
}
</style>
